/**
 * MVP-500 Commit A. "โหมดหน้าร้าน" (storefront mode): the per-machine state behind a deliberately
 * reduced console — a room PC left running in front of the bar, where whoever wanders up to it is
 * not the KJ. This module is the state and its persistence only; the destructive-key lock (Commit
 * B) and the eXtreme keymap (Commit C) read the same boolean and are not here.
 *
 * === WHY THIS IS NOT TIED TO THE BROWSER'S OWN WHOLE-DOCUMENT MODE ===
 * The owner's first ask was "when the screen goes whole-document, switch the keymap". It was argued
 * down and the owner agreed, for one reason: the app is not asked. `lib/useFullscreen.ts` puts
 * `document.documentElement` into that state, `App.css` hides the chrome with a single
 * `html:fullscreen` rule, and F11, ESC and the browser's own UI all move it WITHOUT any React code
 * running — that absence is the ESC guarantee `lib/fullscreen.test.ts` exists to pin. A mode bound
 * to it would therefore rebind every key under the KJ's fingers on a keystroke the app never heard
 * about, with nothing on screen to say it had happened. So: an explicit toggle, a visible
 * indicator, and a state that survives the browser entering or leaving that mode in either
 * direction because it never consulted it in the first place.
 *
 * Storage shape is `features/player/vocalLevelsPrefs.ts`, faithfully: reject-anything-unexpected
 * parsing, `hasLocalStorage()` plus a `try/catch` on both the read and the write (private-mode
 * browsers throw from `getItem`/`setItem`, and the `node` vitest project has no `localStorage`
 * global at all, so an unguarded read would break every test that imports this file). Same
 * per-machine argument as that module's: which machine is the one standing in the room is a fact
 * about the machine, not about tonight's session, so this never goes near Strapi.
 */

/** Exported because a SECOND document reads this through the browser's `storage` event rather than
 * through the functions below — see `isStorefrontStorageEvent`. */
export const STOREFRONT_STORAGE_KEY = 'mvcp.storefront.v1';

/**
 * Pure — anything that is not exactly the boolean `true` is off.
 *
 * It FAILS OPEN on purpose. A blob that was hand-edited, written by an older build, or truncated
 * mid-write resolves to "mode off, everything works", never to "mode on": a reduced console nobody
 * can get out of is a worse night than one that never engaged, because the way out is itself one of
 * the things the mode takes away.
 */
export function parseStorefrontMode(raw: unknown): boolean {
  return raw === true;
}

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

export function loadStorefrontMode(): boolean {
  if (!hasLocalStorage()) return false;
  try {
    const raw = localStorage.getItem(STOREFRONT_STORAGE_KEY);
    if (!raw) return false;
    return parseStorefrontMode(JSON.parse(raw));
  } catch {
    return false; // corrupt/blocked storage is never a crash, and never an engaged mode
  }
}

export function saveStorefrontMode(on: boolean): void {
  if (!hasLocalStorage()) return;
  try {
    localStorage.setItem(STOREFRONT_STORAGE_KEY, JSON.stringify(on));
  } catch {
    /* private-mode/storage-full — losing the preference is fine, throwing is not */
  }
}

/**
 * Pure — decides whether a `storage` event carries a storefront change this document should adopt.
 * Same cross-tab shape as `i18n/locale.ts`'s `isLocaleStorageEvent`: the Stage is a second document
 * (`useStageWindow`'s `window.open`) on the same origin, and the browser's `storage` event is the
 * only signal it ever gets about a preference the cockpit changed.
 *
 * One deliberate difference from that function: a `null` key (the whole store was cleared) is NOT
 * adopted here. `loadLocale()` answers a real locale for an empty store, so re-reading is the right
 * response there; this key's absence is indistinguishable from "never set", and treating a
 * `localStorage.clear()` from some unrelated tab as "the operator asked to leave storefront mode"
 * would drop the lock on the room PC without anyone touching it.
 */
export function isStorefrontStorageEvent(key: string | null): boolean {
  return key === STOREFRONT_STORAGE_KEY;
}
